import { useCallback } from "react";

import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";

import { Button, Card, Header, Icon, Message } from "simplydo/react/ui";

import { useAppSelector } from "store";
import util from "utils/utils";

import { UserChip } from "components/lib/Chips";

import InviteeChip from "./InviteeChip";
import type { InviteCardProps } from "./types";

const IdeaInviteCard = ({
  invitation,
  acceptInvite,
  rejectInvite,
  removeInvite,
  cardView = true,
}: InviteCardProps) => {
  const {
    inviterUser,
    inviteeUser,
    invitee,
    challenge,
    forIdeas = [],
    createdByCurrentUser,
  } = invitation;
  const user = useAppSelector((state) => state.user);
  const { t } = useTranslation();
  const navigate = useNavigate();

  const accept = useCallback(() => {
    acceptInvite(
      invitation,
      () => {
        navigate(`/challenges/${challenge?._id}/assessments/assigned`);
        toast.success(`Became ${t("generic.idea")} assessor`);
      },
      () => toast.error(`Error becoming ${t("generic.idea")} assessor`),
    );
  }, [acceptInvite, invitation, challenge, t, navigate]);

  const reject = useCallback(() => {
    util
      .confirm(
        "Reject assessment invitation",
        `Are you sure you want to reject an invitation from ${inviterUser?.profile?.fullName} to assess ${util.pluralise(forIdeas.length, t("generic.idea"), t("generic.ideas"))} in "${challenge?.name || `Deleted ${t("generic.challenge")}`}?`,
      )
      .then(() => {
        rejectInvite(
          invitation,
          () => {
            toast("Invitation rejected");
            navigate(`/`);
          },
          () => toast.error("Error rejecting invite"),
        );
      })
      .catch(() => {});
  }, [
    rejectInvite,
    invitation,
    inviterUser?.profile?.fullName,
    forIdeas,
    challenge?.name,
    t,
    navigate,
  ]);

  const remove = useCallback(() => {
    util
      .confirm(
        "Remove assessment invitation",
        `Are you sure you want to remove this invitation to ${inviteeUser?.profile?.fullName || invitee} to assess ${t("generic.ideas")} in "${challenge?.name || `Deleted ${t("generic.challenge")}`}"?`,
      )
      .then(() => {
        removeInvite(
          invitation,
          () => {
            toast("Invitation removed");
            navigate(`/challenges/${challenge?._id}/assessments`);
          },
          () => toast.error("Error removing invite"),
        );
      })
      .catch(() => {});
  }, [
    removeInvite,
    invitation,
    challenge?.name,
    inviteeUser?.profile?.fullName,
    invitee,
    t,
    navigate,
    challenge?._id,
  ]);

  const canAccept = invitation.canAccept && !!challenge;

  return (
    <div
      style={
        cardView ?
          {
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }
        : {}
      }
    >
      <div>
        {cardView ?
          <Header
            style={{ marginBottom: "0.3em" }}
            as={Link}
            disabled={!challenge}
            to={challenge ? `/challenges/${challenge._id}` : null}
          >
            {challenge?.name || `Deleted ${t("generic.challenge")}`}
          </Header>
        : <Header as="h2" icon textAlign="center">
            <Icon name="lightbulb" circular />
            <Header.Content>
              Assess {challenge?.name || `Deleted ${t("generic.challenge")}`}
            </Header.Content>
          </Header>
        }
        <Card.Description>
          {createdByCurrentUser ?
            <InviteeChip
              invitation={invitation}
              preText="You invited"
              postText={`to assess ${util.pluralise(forIdeas.length, t("generic.idea"), t("generic.ideas"))} in this ${t("generic.challenge")}`}
            />
          : <>
              <UserChip user={inviterUser} compact="very" /> has invited you to
              assess{" "}
              {util.pluralise(
                forIdeas.length,
                t("generic.idea"),
                t("generic.ideas"),
              )}{" "}
              in this {t("generic.challenge")}. Accept this invitation to start
              assessing the{" "}
              {util.pluralise(
                forIdeas.length,
                t("generic.idea"),
                t("generic.ideas"),
                false,
              )}{" "}
              in "{challenge?.name || `Deleted ${t("generic.challenge")}`}".
            </>
          }
        </Card.Description>
      </div>
      <Card.Content extra>
        <div
          className="ui"
          style={
            cardView ?
              {}
            : { marginTop: 20, display: "flex", justifyContent: "flex-end" }
          }
        >
          {invitation.acceptedAt && challenge && (
            <Button
              basic
              color="green"
              as={Link}
              to={`/challenges/${challenge?._id}/assessments/assigned`}
            >
              Go to assessments
            </Button>
          )}
          {!invitation.acceptedAt && (
            <>
              {!challenge && (
                <Message error style={{ width: "100%" }}>
                  This invitation to assess is no longer valid because the{" "}
                  {t("generic.challenge")} has been deleted.
                </Message>
              )}
              {user && invitation.canRemove ?
                <Button
                  basic
                  color={createdByCurrentUser ? undefined : "red"}
                  onClick={createdByCurrentUser ? remove : reject}
                >
                  {createdByCurrentUser ? "Delete invitation" : "Reject"}
                </Button>
              : null}
              {createdByCurrentUser && challenge ?
                <Button
                  primary
                  as={Link}
                  to={`/challenges/${challenge._id}/assessments`}
                >
                  Go to assessments
                </Button>
              : null}
              {user && canAccept && !createdByCurrentUser ?
                <Button basic color="green" onClick={accept}>
                  Accept
                </Button>
              : null}
            </>
          )}
        </div>
      </Card.Content>
    </div>
  );
};

export default IdeaInviteCard;
